import "@/App.css";
import "./i18n";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "./context/AuthContext";

// Pages
import LandingPage from "./pages/LandingPage";
import AuthPage from "./pages/AuthPage";
import ContractorDashboard from "./pages/ContractorDashboard";
import ContractorSetup from "./pages/ContractorSetup";
import WalletPage from "./pages/WalletPage";
import ClientDashboard from "./pages/ClientDashboard";
import PostJobPage from "./pages/PostJobPage";
import AdminDashboard from "./pages/AdminDashboard";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import SMSTermsPage from "./pages/SMSTermsPage";
import MessagesPage from "./pages/MessagesPage";
import ReviewPage from "./pages/ReviewPage";
import TipsPage from "./pages/TipsPage";
import AuthCallback from "./pages/AuthCallback";
import ContactPage from "./pages/ContactPage";
import CaliforniaPrivacyPage from "./pages/CaliforniaPrivacyPage";
import AboutPage from "./pages/AboutPage";
import TipDetailPage from "./pages/TipDetailPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-orange-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Redirect to appropriate dashboard based on role
    if (user.role === 'contractor') {
      return <Navigate to="/contractor/dashboard" replace />;
    } else if (user.role === 'client') {
      return <Navigate to="/client/dashboard" replace />;
    } else if (user.role === 'admin') {
      return <Navigate to="/admin/dashboard" replace />;
    }
    return <Navigate to="/" replace />;
  }

  return children;
};

// Public Route - redirects authenticated users
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-orange-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (user) {
    if (user.role === 'contractor') {
      return <Navigate to="/contractor/dashboard" replace />;
    } else if (user.role === 'client') {
      return <Navigate to="/client/dashboard" replace />;
    } else if (user.role === 'admin') {
      return <Navigate to="/admin/dashboard" replace />;
    }
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route
        path="/auth"
        element={
          <PublicRoute>
            <AuthPage mode="login" />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <AuthPage mode="register" />
          </PublicRoute>
        }
      />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* Contractor Routes */}
      <Route
        path="/contractor/dashboard"
        element={
          <ProtectedRoute allowedRoles={['contractor']}>
            <ContractorDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/contractor/setup"
        element={
          <ProtectedRoute allowedRoles={['contractor']}>
            <ContractorSetup />
          </ProtectedRoute>
        }
      />
      <Route
        path="/contractor/wallet"
        element={
          <ProtectedRoute allowedRoles={['contractor']}>
            <WalletPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/contractor/profile"
        element={
          <ProtectedRoute allowedRoles={['contractor']}>
            <ContractorSetup />
          </ProtectedRoute>
        }
      />

      {/* Client Routes */}
      <Route
        path="/client/dashboard"
        element={
          <ProtectedRoute allowedRoles={['client']}>
            <ClientDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/post-job"
        element={
          <ProtectedRoute allowedRoles={['client']}>
            <PostJobPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/profile"
        element={
          <ProtectedRoute allowedRoles={['client']}>
            <ClientDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/messages/:jobId"
        element={
          <ProtectedRoute allowedRoles={['client']}>
            <MessagesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/review/:jobId"
        element={
          <ProtectedRoute allowedRoles={['client']}>
            <ReviewPage />
          </ProtectedRoute>
        }
      />

      {/* Contractor Message Route */}
      <Route
        path="/contractor/messages/:jobId"
        element={
          <ProtectedRoute allowedRoles={['contractor']}>
            <MessagesPage />
          </ProtectedRoute>
        }
      />

      {/* Admin Routes */}
      <Route
        path="/admin/dashboard"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />

      {/* Legal Pages - Public */}
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/sms-terms" element={<SMSTermsPage />} />
      <Route path="/tips" element={<TipsPage />} />
      <Route path="/tips/:slug" element={<TipDetailPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/california-privacy" element={<CaliforniaPrivacyPage />} />
      <Route path="/ccpa" element={<CaliforniaPrivacyPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* Catch-all redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
